import { useTranslation } from 'react-i18next';
import { MILESTONE_TIER_NAMES, PLAYER_TITLES } from '../constants/economy';
/**
 * Module 132 (WP-132.6) — the words every milestone surface (win line, toast,
 * home peek, and from WP-132.10 the Milestones and Badges popups) names milestones with: the same keys and the same "Platinum ★n"
 * shape as the Milestones and Badges popups.
 */
export function useMilestoneLabels() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'en';
    /** A rung's name: a tier, or "Platinum ★n" for a step. */
    const tierLabel = (index) => {
        if (index < MILESTONE_TIER_NAMES.length)
            return t(`blockjam.milestones.tier.${MILESTONE_TIER_NAMES[index]}`);
        return `${t('blockjam.milestones.tier.platinum')} ★${index - MILESTONE_TIER_NAMES.length + 1}`;
    };
    /** "12 more puzzles for Gold" (one: "1 more puzzle for Gold"). */
    const moreFor = (id, left, index) => {
        const n = Math.max(1, left);
        return t('blockjam.badges.more', {
            n: n.toLocaleString(lang),
            unit: t(n === 1 ? `blockjam.badges.unitOne.${id}` : `blockjam.badges.unit.${id}`),
            tier: tierLabel(index),
        });
    };
    return {
        t,
        lang,
        nf: (n) => n.toLocaleString(lang),
        /**
         * WP-132.14 — a stop's number, short from 1,000 ("2.5K", "10K") so four fit on a bar.
         * "K" in every language: the locales' own short forms ("2,5 tys.", "2.5 हज़ार") are too
         * wide for a stop.
         */
        cnf: (n) => n < 1000
            ? n.toLocaleString(lang)
            : `${(n / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })}K`,
        name: (id) => t(`blockjam.milestones.name.${id}`),
        tierLabel,
        /** A medal's name: its tier, or "Platinum ★n" once it has ★ steps. */
        medalLabel: (tier, stars) => tierLabel(stars > 0 ? MILESTONE_TIER_NAMES.length - 1 + stars : tier),
        moreFor,
        /** WP-132.11 — "Explorer", or "Legend ★2" past the last title. */
        titleLabel: (title) => `${t(`blockjam.milestones.titles.${PLAYER_TITLES[title.index]}`)}${title.stars > 0 ? ` ★${title.stars}` : ''}`,
        /** WP-132.11 — "6 badges · 2 more for Solver" (Legend: "… for Legend ★3"). */
        titleLine: (title) => {
            const next = title.index < PLAYER_TITLES.length - 1
                ? t(`blockjam.milestones.titles.${PLAYER_TITLES[title.index + 1]}`)
                : `${t(`blockjam.milestones.titles.${PLAYER_TITLES[title.index]}`)} ★${title.stars + 1}`;
            const count = t(title.badges === 1 ? 'blockjam.milestones.badgeCountOne' : 'blockjam.milestones.badgeCount', { n: title.badges.toLocaleString(lang) });
            const more = t('blockjam.milestones.titleMore', {
                n: (title.nextAt - title.badges).toLocaleString(lang),
                title: next,
            });
            return `${count} · ${more}`;
        },
    };
}
/** A rung's medal: a tier, or Platinum with its ★ count for a step. */
export function rungMedal(index) {
    const top = MILESTONE_TIER_NAMES.length - 1;
    return index <= top ? { tier: index, stars: 0 } : { tier: top, stars: index - top };
}
